import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useUserContext } from "../contexts/UserContext.js";
axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.withCredentials = true;

const PrivateRoute = ({ children, requiredRoles, requiredTiers }) => {
  // auth: -1 = unknown, true = authorized, false = unauthorized
  const [auth, setAuth] = useState(-1);
  const [finishedVerifying, setFinishedVerifying] = useState(false);
  const { user, loading } = useUserContext();

  // normalize props: allow a single string or array
  const allowedRoles = Array.isArray(requiredRoles)
    ? requiredRoles
    : requiredRoles
    ? [requiredRoles]
    : null;
  const allowedTiers = Array.isArray(requiredTiers)
    ? requiredTiers
    : requiredTiers
    ? [requiredTiers]
    : null;

  useEffect(() => {
    // Wait for UserContext to finish loading. UserContext sets `loading` while
    // it fetches /user/me on mount. PrivateRoute will block rendering until
    // loading is false, then use user.isAuthenticated for auth decisions.
    if (typeof loading === "undefined") {
      // If for some reason UserContext doesn't provide loading, fallback to
      // consider user immediately available.
      setAuth(Boolean(user?.isAuthenticated));
      setFinishedVerifying(true);
      return;
    }

    if (loading) {
      setFinishedVerifying(false);
      setAuth(-1);
      return;
    }

    // loading === false
    setAuth(Boolean(user?.isAuthenticated));
    setFinishedVerifying(true);
  }, [loading, user]);

  // don't render page until JWT/user context is verified
  if (!finishedVerifying) return <div />; // or some kind of loading animation

  // if not authenticated, send to login
  if (!auth) {
    return (
      <Navigate
        to='/login'
        replace
        state={{ message: "You must be logged in to view this page." }}
      />
    );
  }

  // At this point auth === true. Enforce role/tier requirements if provided.
  const hasRequiredRole = (() => {
    if (!allowedRoles) return true;
    // support user.role (string) or user.roles (array)
    const u = user || {};
    const roles =
      (u.roles && Array.isArray(u.roles) && u.roles) ||
      (u.role && [u.role]) ||
      [];
    return allowedRoles.some((r) => roles.includes(r));
  })();

  const hasRequiredTier = (() => {
    if (!allowedTiers) return true;
    const u = user || {};
    // common places to store subscription tier — adapt as needed
    const tier = u.subscriptionTier || null;
    if (!tier) return false;
    return allowedTiers.includes(tier);
  })();

  if (!hasRequiredRole || !hasRequiredTier) {
    // Authenticated but not authorized for this route
    return <Navigate to='/unauthorized' replace />;
  }

  return children;
};

export default PrivateRoute;
